"use client";

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { message } from "antd";
import { api } from "@api";
import ReactApexChart from "react-apexcharts";
import { getFormattedNumber } from "../../utils";
import moment from "moment";
import { useTranslation } from "react-i18next";

const ChartWrapper = styled.div`
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  background-color: ${(props) => props.theme.backgroundColor};
  border: 1px solid ${(props) => props.theme.primaryColor};
  font-family: "Inter", sans-serif;

  .title {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 4px;
  }

  .total-income {
    font-size: 24px;
    font-weight: bold;
  }

  .growth {
    color: #4ade80;
    font-size: 14px;
    margin-bottom: 16px;
  }

  .switch {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
    button {
      padding: 4px 12px;
      border-radius: 6px;
      border: 1px solid #ddd;
      background: white;
      cursor: pointer;
      &.active {
        background: #1677ff;
        color: white;
        border-color: #1677ff;
      }
    }
  }
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 10px;
  }
  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;
`;

const ChartContainer = styled.div`
  // min-width: 600px;
  overflow: hidden;
`;

const IncomeChart = ({ currency_code }) => {
  const [duration, setDuration] = useState("daily");
  const [chartData, setChartData] = useState();

  const {t} = useTranslation();

  const getIncomeChart = (filter) => {
    api
      .getIncomeChart(filter)
      .then((res) => {
        setChartData(res);
      })
      .catch((err) => message.error(err));
  };

  useEffect(() => {
    getIncomeChart({ type: duration });
  }, [duration]);

  const chartSeries = [
    {
      name: "Order",
      data:
        chartData?.data?.map((item) => ({
          x: item.period,
          y: item.order_count,
        })) || [],
    },
  ];

  const chartOptions = {
    chart: {
      type: "line",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      markers: {
        shape: ["line"],
        strokeWidth: 2,
        fillColors: ["#1677ff"],
      },
    },
    stroke: {
      curve: "smooth",
      width: 2,
      colors: ["#1677ff"],
    },
    markers: {
      size: 3,
      colors: ["#1677ff"],
      hover: { size: 5 },
      shape: ["circle"],
    },
    grid: {
      padding: {
        right: 40,
      },
      row: {
        colors: ["transparent"], // takes an array which will be repeated on columns
        opacity: 0.5,
      },
      strokeDashArray: 3,
    },
    xaxis: {
      tooltip: {
        enabled: false,
      },
      // categories: categories,
      type: "datetime",
      tickAmount: duration === "yearly" ? 2 : duration === "monthly" ? 5 : 4,
      labels: {
        formatter(value, timestamp, opts) {
          return duration === "yearly"
            ? moment(value).format("YYYY")
            : duration === "monthly"
            ? moment(value).format("MMM YY")
            : moment(value).format("MMM D");
        },
      },
      tickPlacement: "on",
    },
    yaxis: {
      tickAmount: 5,
      labels: {
        formatter: (value) => getFormattedNumber(value),
      },
      min: 0,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (value) => `${getFormattedNumber(value)}`,
      },
      x: {
        formatter(value, timestamp, opts) {
          return duration === "yearly"
            ? moment(value).format("YYYY")
            : duration === "monthly"
            ? moment(value).format("MMM YYYY")
            : moment(value).format("MMM D");
        },
      },
      marker: {
        fillColors: ["#1677ff"],
      },
    },
  };
  return (
    <ChartWrapper>
      <div style={{ padding: "24px 24px 0 24px" }}>
        <div className="title">{t('total_orders')}</div>
        <div className="total-income">
        {chartData?.total_orders}
      </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <div className="growth">
            {chartData?.order_status === "Up"
              ? `↑ ${chartData?.percent} than `
              : chartData?.order_status === "Down"
              ? `↓ ${chartData?.percent} than `
              : `Equal to `}
            {duration === "daily"
              ? "yesterday"
              : duration === "monthly"
              ? "last month"
              : duration === "yearly"
              ? "last year"
              : ""}
          </div>
          <div className="switch">
            {["daily", "monthly", "yearly"].map((type) => (
              <button
                key={type}
                onClick={() => setDuration(type)}
                className={duration === type ? "active" : ""}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <ScrollContainer>
        <ChartContainer>
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={250}
          />
        </ChartContainer>
      </ScrollContainer>
    </ChartWrapper>
  );
};

export default IncomeChart;
